// The restored blog's public pages (/blog and /blog/<slug>) for src/lib/seo/pages.ts: live everywhere (not behind the
// guides flag). A post a guide now covers is a 301 to that guide where the guides are on, so there it's left out
// (no head, no sitemap entry); where they're off, it's an ordinary post.

import { BLOG_INDEX_SEO, BLOG_PATH, BLOG_POSTS, blogPath, postImages, type BlogPost } from "@/content/blog";
import { plainInline } from "@/content/guides/body";
import { findAuthor } from "@/content/guides/authors";
import { getMedia } from "@/content/media";
import { OG_IMAGES, type OgImage } from "./site";
import type { PageImage, PublicPage, StructuredDataContext } from "./pages";
import { imageObjects } from "./images";

/** An article's link-preview image: its own socialImage, else the card made for its hero
 *  (/images/og/<section>/<slug>.jpg, scripts/content-images/render.mjs), else the site default. */
export const articleOgImage = (p: BlogPost, section: string): OgImage =>
  p.socialImage || p.hero
    ? {
        path: p.socialImage ?? `/images/og/${section}/${p.slug}.jpg`,
        width: 1200,
        height: 630,
        type: "image/jpeg",
        alt: p.socialAlt ?? `${p.title}. Photohawk Studio.`,
      }
    : OG_IMAGES.default;

/** The images an article shows (hero first), for the image sitemap and its ImageObjects. */
export const articlePageImages = (p: BlogPost): PageImage[] =>
  postImages(p).map(({ image, alt, caption }) => {
    const m = getMedia(image)!;
    return { path: m.src, width: m.width, height: m.height, alt, caption: caption ?? m.caption };
  });


const crumbs = (url: string, items: { name: string; url: string }[]) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumbs`,
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.url })),
});

const authorLd = (p: BlogPost, origin: string) => {
  const a = findAuthor(p.author);
  if (!a || a.kind === "team") return { "@type": "Organization", name: a?.name ?? "Photohawk", url: `${origin}/` };
  return { "@type": "Person", name: a.name, jobTitle: a.role, ...(a.sameAs ? { sameAs: a.sameAs } : {}) };
};

export const postStructuredData = (p: BlogPost, { origin, url, image }: StructuredDataContext): object[] => [
  {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: url,
    url,
    headline: p.title,
    description: plainInline(p.description),
    inLanguage: "en",
    image: p.hero ? imageObjects(origin, articleOgImage(p, "blog"), articlePageImages(p)) : image,
    ...(p.hero ? { thumbnailUrl: `${origin}${articleOgImage(p, "blog").path}` } : {}),
    datePublished: p.published,
    dateModified: p.updated ?? p.published,
    author: authorLd(p, origin),
    publisher: { "@id": `${origin}/#organization` },
    isPartOf: { "@type": "Blog", "@id": `${origin}${BLOG_PATH}#blog`, name: "The Photohawk blog", url: `${origin}${BLOG_PATH}` },
    wordCount: p.words,
  },
  crumbs(url, [
    { name: "Home", url: `${origin}/` },
    { name: "Blog", url: `${origin}${BLOG_PATH}` },
    { name: p.title, url },
  ]),
  // Only for the questions the article shows (BlogPages renders each one under "Questions people ask").
  ...(p.faq.length
    ? [
        {
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          mainEntity: p.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: plainInline(f.a) } })),
        },
      ]
    : []),
];

/** /blog and its posts (minus the ones that redirect to a guide when the guides are on). */
export function blogPages(guidesOn: boolean): PublicPage[] {
  const posts = BLOG_POSTS.filter((p) => !(guidesOn && p.supersededBy));
  const index: PublicPage = {
    path: BLOG_PATH,
    langs: ["en"],
    seo: () => BLOG_INDEX_SEO,
    image: OG_IMAGES.default,
    type: "website",
    indexable: true,
    changefreq: "monthly",
    priority: 0.4,
    structuredData: ({ origin, url, title, description }) => [
      {
        "@type": "Blog",
        "@id": `${url}#blog`,
        url,
        name: "The Photohawk blog",
        headline: title,
        description,
        inLanguage: "en",
        publisher: { "@id": `${origin}/#organization` },
        blogPost: posts.map((p) => ({ "@type": "BlogPosting", headline: p.title, url: `${origin}${blogPath(p.slug)}`, datePublished: p.published })),
      },
      crumbs(url, [
        { name: "Home", url: `${origin}/` },
        { name: "Blog", url },
      ]),
    ],
  };
  return [
    index,
    ...posts.map(
      (p): PublicPage => ({
        path: blogPath(p.slug),
        langs: ["en"],
        // "<title> | Photohawk", without the suffix when that would run past 60 characters.
        seo: () => ({ title: `${p.metaTitle} | Photohawk`.length <= 60 ? `${p.metaTitle} | Photohawk` : p.metaTitle, description: p.description }),
        image: articleOgImage(p, "blog"),
        images: articlePageImages(p),
        type: "article",
        indexable: true,
        changefreq: "yearly",
        priority: 0.3,
        lastmod: p.updated ?? p.published,
        structuredData: (ctx) => postStructuredData(p, ctx),
      }),
    ),
  ];
}
